import { TrialExtensionModalWrapper } from '@/components/modal/trial-extension/TrialExtensionModalWrapper';
import { invalidateGamifyTrialExtension } from '@/hooks/query/useQueryGamifyTrialExtension';
import { useEmailInviteForm } from '@/hooks/useEmailInviteForm';
import { trackButtonClicked } from '@/lib/analytics/scribe/button';
import { trackModalViewed } from '@/lib/analytics/scribe/ui';
import { cn } from '@/lib/cn';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import {
  trialExtensionInviteModalOpenAtom,
  trialExtensionModalOpenAtom,
} from '@heidi/commerce/data';
import { Button } from '@heidi/design-system/button';
import { Input } from '@heidi/design-system/input';
import {
  TypographyH2,
  TypographyP,
  TypographyP2,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtom, useSetAtom } from 'jotai';
import { Check, Plus, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect } from 'react';

export function GamifyTrialExtensionInviteModal() {
  const [isOpen, setIsOpen] = useAtom(trialExtensionInviteModalOpenAtom);
  const setTrialExtensionModalOpen = useSetAtom(trialExtensionModalOpenAtom);
  const { t } = useTranslations();

  const {
    emails,
    emailErrors,
    invitationStatus,
    isPending,
    resetForm,
    handleAddEmail: baseHandleAddEmail,
    handleRemoveEmail,
    handleEmailChange,
    handleSendInvites: baseHandleSendInvites,
  } = useEmailInviteForm({
    onSuccess: () => void invalidateGamifyTrialExtension(),
    onClose: () => {
      setIsOpen(false);
      resetForm();
      setTrialExtensionModalOpen(true);
    },
  });

  // Track modal open
  useEffect(() => {
    if (isOpen) {
      trackModalViewed({
        screenName: ScreenName.TrialExtensionInviteModal,
      });
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    trackButtonClicked({
      screenName: ScreenName.TrialExtensionInviteModal,
      action: ButtonAction.TrialExtensionClose,
      label: 'Close',
    });
    setIsOpen(false);
    resetForm();
  }, [setIsOpen, resetForm]);

  const handleBack = useCallback(() => {
    trackButtonClicked({
      screenName: ScreenName.TrialExtensionInviteModal,
      action: ButtonAction.TrialExtensionBack,
      label: 'Back',
    });
    handleClose();
    setTrialExtensionModalOpen(true);
  }, [handleClose, setTrialExtensionModalOpen]);

  const handleAddEmail = useCallback(() => {
    trackButtonClicked({
      screenName: ScreenName.TrialExtensionInviteModal,
      action: ButtonAction.TrialExtensionAddEmail,
      label: 'Add another',
    });
    baseHandleAddEmail();
  }, [baseHandleAddEmail]);

  const handleSendInvites = useCallback(async () => {
    trackButtonClicked({
      screenName: ScreenName.TrialExtensionInviteModal,
      action: ButtonAction.TrialExtensionSendInvites,
      label: 'Send invites',
    });
    await baseHandleSendInvites();
  }, [baseHandleSendInvites]);

  if (isAppleCompliantBuild()) return null;

  return (
    <TrialExtensionModalWrapper isOpen={isOpen} onClose={handleClose}>
      <div className="flex flex-col gap-0.5">
        <TypographyH2>{t('trialExtension.inviteTitle')}</TypographyH2>
        <TypographyP2 className="text-text-secondary">
          {t('trialExtension.inviteDescription')}
        </TypographyP2>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex w-full flex-col gap-1">
          <TypographyP2Bold>{t('trialExtension.teamMemberEmail')}</TypographyP2Bold>
          <div className="flex max-h-52 w-full flex-col gap-3 overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {emails.map((email, index) => (
              <div key={index} className="flex w-full flex-col gap-1">
                <div className="flex w-full items-center gap-2">
                  <Input
                    value={email}
                    onChange={(e) => handleEmailChange(index, e.target.value)}
                    wrapperClassName="flex-1"
                    className={cn(emailErrors[index] && 'border-validation-destructive')}
                    type="email"
                    placeholder=""
                  />
                  {emails.length > 1 && (
                    <Button
                      size="lg"
                      variant="ghost"
                      onClick={() => handleRemoveEmail(index)}
                      aria-label={t('common.remove')}
                    >
                      <X />
                    </Button>
                  )}
                </div>
                {emailErrors[index] && (
                  <TypographyP2 className="text-validation-destructive">
                    {t('trialExtension.invalidEmail')}
                  </TypographyP2>
                )}
              </div>
            ))}
          </div>
        </div>

        <Button
          size="lg"
          variant="ghost"
          onClick={handleAddEmail}
          className="flex w-fit items-center gap-1 px-0 text-text-secondary hover:bg-transparent hover:text-text-primary"
        >
          <Plus size={24} />
          <TypographyP className="font-medium">
            {t('trialExtension.addAnother')}
          </TypographyP>
        </Button>

        {invitationStatus && (
          <div className="flex flex-col gap-1">
            {invitationStatus.successCount > 0 && (
              <div className="flex items-center gap-2">
                <Check size={16} className="text-validation-success" />
                <TypographyP2 className="text-validation-success">
                  {t('trialExtension.invitesSent', {
                    count: invitationStatus.successCount,
                  })}
                </TypographyP2>
              </div>
            )}
            {invitationStatus.failureCount > 0 && (
              <div className="flex items-center gap-2">
                <TriangleAlert size={16} className="text-validation-destructive" />
                <TypographyP2 className="text-validation-destructive">
                  {t('trialExtension.invitesFailed', {
                    count: invitationStatus.failureCount,
                  })}
                </TypographyP2>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4 pt-10">
        <Button
          className="w-full"
          size="lg"
          onClick={() => void handleSendInvites()}
          disabled={isPending}
        >
          {isPending ? t('trialExtension.sending') : t('trialExtension.sendInvites')}
        </Button>
        <Button
          variant="outline"
          className="w-full"
          size="lg"
          onClick={handleBack}
          disabled={isPending}
        >
          {t('common.back')}
        </Button>
      </div>
    </TrialExtensionModalWrapper>
  );
}
